package com.evoapps.evoshift.ui.kiosk

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.TabletAndroid
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.model.KioskDevice
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.KioskDevicesViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.ProfileSection
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.kiosk_code_generate
import evoshift.composeapp.generated.resources.kiosk_code_text
import evoshift.composeapp.generated.resources.kiosk_device_last_seen
import evoshift.composeapp.generated.resources.kiosk_device_revoked
import evoshift.composeapp.generated.resources.kiosk_pair_code
import evoshift.composeapp.generated.resources.kiosk_qr_setting
import evoshift.composeapp.generated.resources.kiosk_qr_setting_help
import evoshift.composeapp.generated.resources.kiosk_revoke
import evoshift.composeapp.generated.resources.kiosk_revoke_text
import evoshift.composeapp.generated.resources.kiosk_revoke_title
import evoshift.composeapp.generated.resources.kiosk_settings_help
import evoshift.composeapp.generated.resources.kiosk_settings_name_hint
import evoshift.composeapp.generated.resources.kiosk_settings_none
import evoshift.composeapp.generated.resources.kiosk_settings_pair
import evoshift.composeapp.generated.resources.kiosk_settings_title
import org.jetbrains.compose.resources.stringResource
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/**
 * SH-107, Workplace settings → "Clock-in tablet": the workplace's tablets (last used, unpaired),
 * a one-time pairing code for a new one, and unpairing. Managers only (the screen's gate).
 * [onMessage] shows confirmations and errors in the screen's snackbar.
 */
@Composable
fun KioskDevicesSection(workplaceId: String, onMessage: suspend (String) -> Unit, canEdit: Boolean) {
    val viewModel: KioskDevicesViewModel = diViewModel(KioskDevicesKey(workplaceId))
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    var pairing by remember { mutableStateOf(false) }

    val text = (state.error ?: state.message)?.asString()
    LaunchedEffect(text) {
        text?.let {
            onMessage(it)
            viewModel.messageShown()
        }
    }

    ProfileSection(
        title = stringResource(Res.string.kiosk_settings_title),
        icon = Icons.Default.TabletAndroid,
        onEdit = null,
        help = stringResource(Res.string.kiosk_settings_help)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (!state.isLoading && state.devices.isEmpty()) {
                Text(stringResource(Res.string.kiosk_settings_none), color = DeepNavy.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
            }
            state.devices.forEach { device -> DeviceRow(device, canEdit) { viewModel.askRevoke(device) } }
            // SH-111: the rotating QR on the tablets (staff scan it in the app instead of the PIN).
            Row(Modifier.fillMaxWidth().padding(vertical = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.weight(1f)) {
                    Text(stringResource(Res.string.kiosk_qr_setting), color = DeepNavy, fontWeight = FontWeight.Bold)
                    Text(stringResource(Res.string.kiosk_qr_setting_help), color = DeepNavy.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
                }
                Switch(
                    checked = state.qrEnabled,
                    onCheckedChange = viewModel::setQrEnabled,
                    enabled = canEdit,
                    modifier = Modifier.padding(start = 12.dp).testTag(KIOSK_QR_SWITCH_TAG)
                )
            }
            if (canEdit) {
                TextButton(onClick = { pairing = true }) { Text(stringResource(Res.string.kiosk_settings_pair), color = DeepNavy, fontWeight = FontWeight.Bold) }
            }
        }
    }

    if (pairing) {
        val code = state.code
        if (code == null) {
            EvoDialog(
                title = stringResource(Res.string.kiosk_settings_pair),
                subtitle = stringResource(Res.string.kiosk_settings_help),
                icon = Icons.Default.TabletAndroid,
                onDismiss = { pairing = false },
                confirm = DialogAction(stringResource(Res.string.kiosk_code_generate), viewModel::createCode, loading = state.isCreatingCode)
            ) {
                EvoTextField(value = state.name, onValueChange = viewModel::name, label = stringResource(Res.string.kiosk_settings_name_hint))
            }
        } else {
            EvoDialog(
                title = stringResource(Res.string.kiosk_pair_code),
                icon = Icons.Default.TabletAndroid,
                onDismiss = { viewModel.closeCode(); pairing = false },
                dismiss = null,
                confirm = DialogAction(stringResource(Res.string.close), { viewModel.closeCode(); pairing = false })
            ) {
                Text(
                    code.code.chunked(4).joinToString(" "),
                    color = LightGold,
                    fontSize = 40.sp,
                    fontWeight = FontWeight.Bold,
                    fontFamily = FontFamily.Monospace,
                    modifier = Modifier.fillMaxWidth()
                )
                Text(stringResource(Res.string.kiosk_code_text, localTime(code.expiresAt)))
            }
        }
    }

    state.revoking?.let { device ->
        EvoDialog(
            title = stringResource(Res.string.kiosk_revoke_title, device.name),
            onDismiss = viewModel::cancelRevoke,
            destructive = true,
            confirm = DialogAction(stringResource(Res.string.kiosk_revoke), viewModel::confirmRevoke, destructive = true)
        ) {
            Text(stringResource(Res.string.kiosk_revoke_text))
        }
    }
}

/** SH-111: the QR on/off switch (robots toggle it by this tag). */
const val KIOSK_QR_SWITCH_TAG = "kiosk_qr_switch"

/** Factory key: one view model per workplace (distinct from the settings screen's own key). */
data class KioskDevicesKey(val workplaceId: String)

@Composable
private fun DeviceRow(device: KioskDevice, canEdit: Boolean, onRevoke: () -> Unit) {
    Row(Modifier.fillMaxWidth().padding(vertical = 4.dp), verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f)) {
            Text(device.name, color = DeepNavy, fontWeight = FontWeight.Bold)
            Text(
                if (device.revoked) stringResource(Res.string.kiosk_device_revoked)
                else stringResource(Res.string.kiosk_device_last_seen, device.lastSeenAt?.let { localTime(it) }.orEmpty()),
                color = if (device.revoked) Color(0xFFA4161A) else DeepNavy.copy(alpha = 0.7f),
                style = MaterialTheme.typography.bodySmall
            )
        }
        if (canEdit && !device.revoked) {
            TextButton(onClick = onRevoke) { Text(stringResource(Res.string.kiosk_revoke), color = Color(0xFFA4161A)) }
        }
    }
    Spacer(Modifier.height(2.dp))
}

/** "dd.MM HH:mm" of an ISO instant in this device's zone. */
private fun localTime(iso: String): String = runCatching {
    val t = Instant.parse(iso).toLocalDateTime(TimeZone.currentSystemDefault())
    fun two(n: Int) = n.toString().padStart(2, '0')
    "${two(t.day)}.${two(t.month.ordinal + 1)} ${two(t.hour)}:${two(t.minute)}"
}.getOrDefault(iso)
